import React from 'react';

type TouchHandler = (e: React.TouchEvent) => void;
type MouseHandler = (e: React.MouseEvent) => void;
type PointerHandler = (e: React.PointerEvent) => void;

interface GetPressEventHandlersArgs {
    onTouchStart?: TouchHandler,
    onMouseDown?: MouseHandler,
    onPointerDown?: PointerHandler,
    suppressMouseAfterTouchMs?: number,
}

interface PressEventHandlers {
    onTouchStart?: TouchHandler,
    onMouseDown?: MouseHandler,
    onPointerDown?: PointerHandler,
}

let lastTouchStartTimestamp = 0;
const DEFAULT_SUPPRESS_MOUSE_AFTER_TOUCH_MS = 700;

const detectPointerEventSupport = (): boolean => (
    typeof window !== 'undefined'
    && typeof window.PointerEvent !== 'undefined'
);

const getPressEventHandlers = ({
    onTouchStart,
    onMouseDown,
    onPointerDown,
    suppressMouseAfterTouchMs = DEFAULT_SUPPRESS_MOUSE_AFTER_TOUCH_MS,
}: GetPressEventHandlersArgs): PressEventHandlers => {
    if (!onTouchStart && !onMouseDown && !onPointerDown) {
        return {};
    }

    const handleTouchStart: TouchHandler = (e) => {
        lastTouchStartTimestamp = Date.now();
        if (onTouchStart) {
            onTouchStart(e);
            return;
        }
        if (onMouseDown) {
            (onMouseDown as unknown as (event: React.TouchEvent) => void)(e);
            return;
        }
        if (onPointerDown) {
            (onPointerDown as unknown as (event: React.TouchEvent) => void)(e);
        }
    };

    const handleMouseDown: MouseHandler = (e) => {
        if (e.button !== 0) return;
        if (Date.now() - lastTouchStartTimestamp < suppressMouseAfterTouchMs) return;
        if (onMouseDown) {
            onMouseDown(e);
            return;
        }
        if (onTouchStart) {
            (onTouchStart as unknown as (event: React.MouseEvent) => void)(e);
            return;
        }
        if (onPointerDown) {
            (onPointerDown as unknown as (event: React.MouseEvent) => void)(e);
        }
    };

    const handlePointerDown: PointerHandler = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.pointerType === 'touch') {
            lastTouchStartTimestamp = Date.now();
        }
        if (onPointerDown) {
            onPointerDown(e);
            return;
        }
        if (e.pointerType === 'touch' && onTouchStart) {
            (onTouchStart as unknown as (event: React.PointerEvent) => void)(e);
            return;
        }
        if (e.pointerType === 'mouse' && onMouseDown) {
            (onMouseDown as unknown as (event: React.PointerEvent) => void)(e);
            return;
        }
        if (onMouseDown) {
            (onMouseDown as unknown as (event: React.PointerEvent) => void)(e);
            return;
        }
        if (onTouchStart) {
            (onTouchStart as unknown as (event: React.PointerEvent) => void)(e);
        }
    };

    if (detectPointerEventSupport()) {
        return {
            onPointerDown: handlePointerDown,
        };
    }

    return {
        onTouchStart: handleTouchStart,
        onMouseDown: handleMouseDown,
    };
};

export default getPressEventHandlers;
